import {
  type CaseDto,
  type CatalogItemDto,
  type OfferListItemDto,
  datePl,
  type ShareDto,
  shareCreateSchema,
} from '@bqcrm/shared';
import { Alert, Badge, Button, Checkbox, CopyButton, Group, Modal, ScrollArea, Stack, Table, Text, Textarea, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconCheck, IconCopy, IconSearch } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, errorText } from '../../api';
import { useMe } from '../../auth';
import { TradeBadge, VersionStatusBadge } from '../../components/Badges';
import { Money } from '../../components/Money';
import { QueryState } from '../../components/QueryState';
import { EmptyState } from '../../components/ui';

/** Przeniesione do components/Badges — zostaje dla dotychczasowych importów. */
export { VersionStatusBadge };

/** Wybór pozycji z katalogu (z ceną, jeżeli użytkownik ją widzi). */
export function CatalogPicker({ opened, onClose, onPick, tradeCodes, onCustom }: { opened: boolean; onClose: () => void; onPick: (i: CatalogItemDto) => void; tradeCodes: string[]; onCustom?: () => void }) {
  const [q, setQ] = useState('');
  const [debounced] = useDebouncedValue(q, 250);
  const [onlyTrades, setOnlyTrades] = useState(true);
  const [showSmall, setShowSmall] = useState(false);
  // „materiały drobne” to oznaczenie z modułu magazynu — bez niego katalog w ofercie jak dawniej (wszystko)
  const wh = !!useMe().data?.features.magazyn;
  const list = useQuery({
    queryKey: ['catalog', 'picker', debounced],
    queryFn: () => api<CatalogItemDto[]>(`/catalog?active=true${debounced.trim() ? `&q=${encodeURIComponent(debounced.trim())}` : ''}`),
    enabled: opened,
  });
  // widok oferty: bez materiałów drobnych (chyba że włączone albo wyszukane wprost); produkt wspólny pasuje do każdej branży
  const items = (list.data ?? []).filter(
    (i) => (!onlyTrades || !i.tradeCodes.length || i.tradeCodes.some((t) => tradeCodes.includes(t))) && (!wh || showSmall || !i.hideInOffers || !!debounced.trim()),
  );
  return (
    <Modal opened={opened} onClose={onClose} title="Dodaj z katalogu" size="xl">
      <Group mb="sm" wrap="wrap">
        <TextInput placeholder="Szukaj w katalogu…" leftSection={<IconSearch size={16} />} value={q} onChange={(e) => setQ(e.currentTarget.value)} style={{ flex: 1 }} data-autofocus />
        <Checkbox label="Tylko branże tej oferty" checked={onlyTrades} onChange={(e) => setOnlyTrades(e.currentTarget.checked)} />
        {wh && <Checkbox label="Pokaż materiały drobne" checked={showSmall} onChange={(e) => setShowSmall(e.currentTarget.checked)} />}
      </Group>
      <ScrollArea h={420}>
        <QueryState isLoading={list.isLoading} error={list.error}>
        <Table highlightOnHover>
          <Table.Tbody>
            {items.map((i) => (
              <Table.Tr key={i.id} style={{ cursor: 'pointer' }} onClick={() => onPick(i)}>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    {i.tradeCodes.map((t) => (
                      <TradeBadge key={t} code={t} />
                    ))}
                    <div>
                      <Text size="sm" fw={600}>
                        {i.name} {i.kind === 'pakiet' && <Badge size="xs" color="grape" variant="light">pakiet</Badge>}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {[i.code, i.manufacturer, i.model].filter(Boolean).join(' · ')}
                        {i.components.length ? ` · ${i.components.map((c) => c.name).join(', ')}` : ''}
                      </Text>
                    </div>
                  </Group>
                </Table.Td>
                <Table.Td ta="right">
                  <Money gr={i.calcSalePriceGr ?? i.salePriceGr} />
                  {i.basePrice?.stale && (
                    <Badge size="xs" color="orange" variant="light" ml={4}>
                      cena zakupu nieaktualna
                    </Badge>
                  )}
                  <Text size="xs" c="dimmed">
                    netto / {i.unit}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {!items.length && <EmptyState compact title="Brak pozycji w katalogu" hint={q.trim() || onlyTrades ? 'Zmień wyszukiwanie albo odznacz „Tylko branże tej oferty”.' : undefined} />}
        </QueryState>
      </ScrollArea>
      {/* QA qa10302243 P14: pusty albo niepasujący katalog — pozycja spoza katalogu z kosztem zakupu (marża w ofercie) */}
      {!list.isLoading && items.length < 3 && (
        <Group justify="space-between" mt="sm" gap="xs" wrap="wrap">
          <Text size="xs" c="dimmed" style={{ flex: '1 1 260px' }}>
            Nie ma pasującej pozycji? Dodaj pozycję spoza katalogu — wpisz nazwę, cenę sprzedaży i koszt zakupu, żeby oferta pokazała marżę.
          </Text>
          {onCustom && (
            <Button size="xs" variant="light" onClick={() => (onCustom(), onClose())}>
              Dodaj pozycję własną
            </Button>
          )}
        </Group>
      )}
    </Modal>
  );
}

/** Udostępnienie klientowi: jeden link z wybranymi aktualnymi ofertami sprawy (także różnych firm — D-11). */
export function ShareDialog({ opened, onClose, c, preselect, defaultEmail }: { opened: boolean; onClose: () => void; c: Pick<CaseDto, 'id'>; preselect: string[]; defaultEmail: string | null }) {
  const offers = useQuery({ queryKey: ['case-offers', c.id], queryFn: () => api<OfferListItemDto[]>(`/cases/${c.id}/offers`), enabled: opened });
  const me = useMe();
  const shareable = (offers.data ?? []).filter((o) => o.latest.effectiveStatus === 'wydany' || o.latest.effectiveStatus === 'zaakceptowany');
  const [selected, setSelected] = useState<string[]>(preselect);
  const [email, setEmail] = useState(defaultEmail ?? '');
  const [sendEmail, setSendEmail] = useState(true);
  const [message, setMessage] = useState('');
  const [created, setCreated] = useState<ShareDto | null>(null);
  useEffect(() => {
    if (opened) {
      setSelected(preselect);
      setEmail(defaultEmail ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const create = useMutation({
    mutationFn: () => {
      const r = shareCreateSchema.safeParse({ versionIds: selected, recipientEmail: email, sendEmail, message });
      if (!r.success) throw new Error(r.error.issues.map((i) => i.message).join('\n'));
      return api<ShareDto>('/shares', { method: 'POST', body: r.data });
    },
    onSuccess: (s) => {
      setCreated(s);
      void qc.invalidateQueries({ queryKey: ['case-shares', c.id] });
    },
    onError: (e) => setError(errorText(e)),
  });
  const close = () => {
    setCreated(null);
    setError(null);
    onClose();
  };
  return (
    <Modal opened={opened} onClose={close} title="Udostępnij ofertę klientowi" size="lg">
      {created ? (
        <Stack>
          <Alert color="green" icon={<IconCheck />}>
            Link utworzony{sendEmail ? ` i dodany do wysyłki na ${created.recipientEmail}` : ''}. Ważny do {datePl(created.expiresAt)}.
          </Alert>
          {me.data?.environment.demo && sendEmail && (
            <Alert color="orange" variant="light">
              Środowisko demonstracyjne: wiadomość trafi do testowej skrzynki (Mailpit), nie do prawdziwego klienta.
            </Alert>
          )}
          <Text size="sm">Pełny adres jest widoczny tylko teraz — w systemie zapisywany jest wyłącznie jego skrót.</Text>
          <Group wrap="nowrap">
            <TextInput value={created.url} readOnly style={{ flex: 1 }} />
            <CopyButton value={created.url ?? ''}>
              {({ copied, copy }) => (
                <Button leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />} onClick={copy} variant={copied ? 'light' : 'filled'}>
                  {copied ? 'Skopiowano' : 'Kopiuj'}
                </Button>
              )}
            </CopyButton>
          </Group>
          <Group justify="flex-end">
            <Button variant="default" component="a" href={created.url} target="_blank">
              Otwórz stronę klienta
            </Button>
            <Button onClick={close}>Zamknij</Button>
          </Group>
        </Stack>
      ) : (
        <Stack>
          <Text size="sm" fw={600}>
            Oferty w linku
          </Text>
          {shareable.map((o) => (
            <Checkbox
              key={o.latest.id}
              checked={selected.includes(o.latest.id)}
              onChange={(e) => {
            const on = e.currentTarget.checked;
            setSelected((s) => (on ? [...s, o.latest.id] : s.filter((x) => x !== o.latest.id)));
          }}
              label={
                <Group gap={6}>
                  <Text size="sm">{o.latest.issuedNumber}</Text>
                  <VersionStatusBadge status={o.latest.effectiveStatus} size="xs" />
                  {o.variantTotals.map((v) => (
                    <Text key={v.name} size="xs" c="dimmed">
                      {v.name}: <Money gr={v.grossGr} />
                    </Text>
                  ))}
                </Group>
              }
            />
          ))}
          {!shareable.length && !offers.isLoading && <EmptyState compact title="Brak wydanych ofert do udostępnienia" hint="Udostępnić można ofertę wydaną albo zaakceptowaną." />}
          <TextInput label="E-mail klienta" description="Na ten adres trafi link i kod potwierdzający akceptację." value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
          <Checkbox label="Wyślij link e-mailem" checked={sendEmail} onChange={(e) => setSendEmail(e.currentTarget.checked)} />
          {sendEmail && <Textarea label="Treść wiadomości (opcjonalnie)" description="Powitanie, link i podpis dodajemy automatycznie." autosize minRows={2} value={message} onChange={(e) => setMessage(e.currentTarget.value)} />}
          {error && <Alert color="red">{error}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>
              Anuluj
            </Button>
            <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!selected.length}>
              Utwórz link
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
